import {
  CirclePause,
  Heart,
  Play,
  SkipBack,
  SkipForward,
  CheckCircle2,
  AlertTriangle,
  X,
  Lock,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import digikoreLogo from "../../assets/digikorestudios.png";
import {
  approveWatermarkSong,
  getWatermarkUploadedAudioById,
} from "../../redux/reduxSlices/audioUploadSlice";
// import moment from "moment";
import toast from "react-hot-toast";
import moment from "moment-timezone";

const MusicPlayer = () => {
  const dispatch = useDispatch();
  const { id } = useParams();

  const { getAudioById } = useSelector((state) => state.audioUpload);

  const [songs, setSongs] = useState([]);
  const [selectedSongId, setSelectedSongId] = useState(null);
  const [playingIndex, setPlayingIndex] = useState(null);
  const [likedSongs, setLikedSongs] = useState({});
  const [approvedSongId, setApprovedSongId] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [isLocked, setIsLocked] = useState(false);

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const audioRefs = useRef([]);

  // ✅ Prevent "timeupdate" from fighting with the slider while dragging
  const isSeekingRef = useRef(false);

  useEffect(() => {
    if (id) {
      dispatch(getWatermarkUploadedAudioById(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (getAudioById?.songs?.length) {
      const formattedSongs = getAudioById.songs.map((song) => ({
        id: song.song_id,
        title: song.filename,
        url: song.url,
        uploaded_at: song.uploaded_at,
        approved: song.approved || false,
        approved_at: song.approved_at,
        approved_by: song.approved_by,
      }));
      setSongs(formattedSongs);

      setIsLocked(getAudioById.locked || false);

      if (getAudioById.approved_song_id) {
        setApprovedSongId(getAudioById.approved_song_id);
        setSelectedSongId(getAudioById.approved_song_id);
      }
    } else {
      setSongs([]);
      setPlayingIndex(null);
      setCurrentTime(0);
      setDuration(0);
    }
  }, [getAudioById]);

  // ✅ Keep currentTime/duration in sync with the currently playing audio
  useEffect(() => {
    if (playingIndex === null) return;

    const currentAudio = audioRefs.current[playingIndex];
    if (!currentAudio) return;

    const updateTime = () => {
      if (isSeekingRef.current) return;
      setCurrentTime(currentAudio.currentTime || 0);
    };

    const updateDuration = () => {
      const d = currentAudio.duration;
      setDuration(!d || isNaN(d) ? 0 : d);
    };

    currentAudio.addEventListener("timeupdate", updateTime);
    currentAudio.addEventListener("loadedmetadata", updateDuration);

    // initialize quickly
    updateDuration();
    updateTime();

    return () => {
      currentAudio.removeEventListener("timeupdate", updateTime);
      currentAudio.removeEventListener("loadedmetadata", updateDuration);
    };
  }, [playingIndex]);

  useEffect(() => {
    const isMobile = () =>
      /Android|iPhone|iPad|iPod|Opera Mini|IEMobile/i.test(navigator.userAgent) ||
      window.innerWidth < 768;

    if (isMobile()) return;

    const handleKeyDown = (e) => {
      if (
        e.keyCode === 123 ||
        (e.ctrlKey && e.shiftKey && (e.key === "I" || e.key === "J")) ||
        (e.ctrlKey && e.key === "u")
      ) {
        e.preventDefault();
        alert("Inspect is disabled on this page.");
      }
    };

    const handleContextMenu = (e) => e.preventDefault();

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("contextmenu", handleContextMenu);

    const threshold = 160;
    const checkDevTools = setInterval(() => {
      const widthGap = window.outerWidth - window.innerWidth;
      const heightGap = window.outerHeight - window.innerHeight;

      if (widthGap > threshold || heightGap > threshold) {
        alert("Developer tools detected! Please close them to continue.");
        window.location.reload();
      }
    }, 1000);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("contextmenu", handleContextMenu);
      clearInterval(checkDevTools);
    };
  }, []);

  const togglePlay = (index) => {
    const currentAudio = audioRefs.current[index];
    if (!currentAudio) return;

    audioRefs.current.forEach((audio, i) => {
      if (audio && i !== index) {
        audio.pause();
        audio.currentTime = 0;
      }
    });

    if (playingIndex === index) {
      currentAudio.pause();
      setPlayingIndex(null);
      // keep currentTime displayed (optional)
    } else {
      setCurrentTime(0);
      setDuration(0);

      currentAudio
        .play()
        .then(() => setPlayingIndex(index))
        .catch((err) => console.error("Playback failed:", err));
    }
  };

  const handleSelectSong = (songId, event) => {
    if (isLocked) return;
    event?.stopPropagation();
    setSelectedSongId((prev) => (prev === songId ? null : songId));
  };

  const handleApproveClick = () => {
    if (isLocked) {
      alert(
        "This order is already locked and approved. No further changes can be made.",
      );
      return;
    }
    if (selectedSongId) setShowApprovalModal(true);
    else alert("Please select a song first!");
  };

  const handleApproveConfirm = async () => {
    if (!selectedSongId) return;

    try {
      const payload = { order_id: id, song_id: selectedSongId };
      const response = await dispatch(approveWatermarkSong(payload)).unwrap();
      if (response?.data?.success) {
        await dispatch(getWatermarkUploadedAudioById(id));
        toast.success("Audio approved successfully!");
      }
      setShowApprovalModal(false);
    } catch (error) {
      console.log("error", error);
      toast.error("Failed to approve audio. Please try again.");
      setShowApprovalModal(false);
    }
  };

  const handleApproveCancel = () => setShowApprovalModal(false);

  const toggleLike = (songId, event) => {
    event?.stopPropagation();
    setLikedSongs((prev) => ({ ...prev, [songId]: !prev[songId] }));
  };

  // ✅ DRAGGABLE SEEK
  const handleSeekStart = () => {
    isSeekingRef.current = true;
  };

  const seekTo = (newTime) => {
    const currentAudio =
      playingIndex !== null ? audioRefs.current[playingIndex] : null;
    if (!currentAudio) return;

    const d = currentAudio.duration;
    const safeDuration = !d || isNaN(d) ? 0 : d;

    const clamped =
      safeDuration > 0 ? Math.min(Math.max(newTime, 0), safeDuration) : 0;

    currentAudio.currentTime = clamped;
    setCurrentTime(clamped);
  };

  const handleProgressChange = (e) => {
    const newTime = parseFloat(e.target.value);
    seekTo(newTime);
  };

  const handleSeekEnd = (e) => {
    const newTime = parseFloat(e.target.value);
    seekTo(newTime);
    isSeekingRef.current = false;
  };

  const formatTime = (time) => {
    if (time === null || time === undefined || isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "- /-/--";
    try {
      return new Date(dateString).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  const skipForward = () => {
    if (playingIndex === null || songs.length === 0) return;
    const nextIndex = (playingIndex + 1) % songs.length;
    togglePlay(nextIndex);
  };

  const skipBackward = () => {
    if (playingIndex === null || songs.length === 0) return;
    const prevIndex = playingIndex === 0 ? songs.length - 1 : playingIndex - 1;
    togglePlay(prevIndex);
  };

  const approvedSong = songs.find((song) => song.id === approvedSongId);

  const progressPercent =
    duration > 0
      ? Math.min(100, Math.max(0, (currentTime / duration) * 100))
      : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 pb-20">
      {/* Approval Confirmation Modal */}
      {showApprovalModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-100 to-orange-100 flex items-center justify-center">
                  <AlertTriangle className="text-yellow-600" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Confirm Approval
                  </h3>
                  <p className="text-sm text-gray-500">
                    Are you sure about this action?
                  </p>
                </div>
              </div>
              <button
                onClick={handleApproveCancel}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <div className="mb-6">
              <div className="bg-yellow-50 border border-yellow-100 rounded-xl p-4 mb-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle
                    className="text-yellow-500 mt-0.5 flex-shrink-0"
                    size={18}
                  />
                  <div>
                    <p className="text-sm font-medium text-yellow-800 mb-1">
                      Important Notice
                    </p>
                    <p className="text-xs text-yellow-700">
                      Once you approve a song, this action cannot be undone. The
                      order will be locked and no further changes can be made.
                    </p>
                  </div>
                </div>
              </div>

              {selectedSongId && (
                <div className="bg-gray-50 rounded-xl p-4 mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Selected Song:
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center">
                      <Play size={16} className="text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 truncate">
                        {songs.find((s) => s.id === selectedSongId)?.title}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatDate(
                          songs.find((s) => s.id === selectedSongId)
                            ?.uploaded_at,
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <p className="text-gray-600 text-sm">
                This will mark the selected song as approved for production and
                lock the order. Please verify this is the final version you want
                to proceed with.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleApproveCancel}
                className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleApproveConfirm}
                className="flex-1 py-3 px-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
              >
                <CheckCircle2 size={18} />
                Yes, Approve & Lock
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-md mx-auto px-4 pt-8">
        {/* Header */}
        <div className="flex flex-col items-center justify-center mb-8">
          <img src={digikoreLogo} alt="Digikore Logo" className="h-12 mb-6" />
          <div className="text-center">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mb-2">
              Your Jingles Preview
            </h1>
            <p className="text-gray-600 text-sm">
              {isLocked
                ? "Approved Selection (Locked)"
                : "Listen and select your preferred track"}
            </p>
          </div>
        </div>

        {!isLocked && (
          <div className="mb-4 px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl text-left">
            <p className="text-sm text-blue-700 font-medium">
              <span className="font-semibold">
                To approve jingle for final delivery:
              </span>{" "}
              Click radio button • Click "Approve Selected Song" button
            </p>
          </div>
        )}

        {/* Order Status Banner */}
        {isLocked && (
          <div className="mb-6">
            <div className="bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-2xl p-4 shadow-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center">
                    <Lock className="text-white" size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold text-emerald-800">
                      Order Approved & Locked
                    </h3>
                    <p className="text-sm text-emerald-600">
                      This order has been finalized. No further changes can be
                      made.
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-emerald-500">Order ID:</p>
                  <p className="text-sm font-semibold text-emerald-700">
                    {getAudioById?.order_id || "N/A"}
                  </p>
                </div>
              </div>

              {approvedSong && (
                <div className="mt-4 pt-4 border-t border-emerald-100">
                  <p className="text-sm font-medium text-emerald-700 mb-2">
                    Approved Song:
                  </p>
                  <div className="flex items-center gap-3 bg-white/80 rounded-xl p-3">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-emerald-100 to-green-100 flex items-center justify-center">
                      <CheckCircle2 className="text-emerald-600" size={20} />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 truncate">
                        {approvedSong.title}
                      </p>
                      <div className="flex items-center gap-4 mt-1">
                        <p className="text-xs text-emerald-600">
                          Approved on: {formatDate(approvedSong.approved_at)}
                        </p>
                        <p className="text-xs text-emerald-600">
                          By: {approvedSong.approved_by || "Unknown"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Stats Bar */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/20 mb-6">
          <div className="flex justify-between items-center">
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">
                {songs.length}
              </p>
              <p className="text-xs text-gray-600">Total Jingles</p>
            </div>

            <div className="text-center">
              <div className="flex items-center gap-2">
                <div
                  className={`w-3 h-3 rounded-full ${
                    isLocked
                      ? "bg-emerald-500"
                      : selectedSongId
                        ? "bg-green-500"
                        : "bg-yellow-500"
                  }`}
                ></div>
                <p className="text-sm font-semibold text-gray-700">
                  {isLocked
                    ? "Locked"
                    : selectedSongId
                      ? "Selected"
                      : "Not Selected"}
                </p>
              </div>
              {selectedSongId && (
                <p className="text-xs text-gray-500 mt-1 truncate max-w-[120px]">
                  {songs.find((s) => s.id === selectedSongId)?.title}
                </p>
              )}
            </div>

            <div className="text-center">
              <div
                className={`px-3 py-1 rounded-full ${
                  isLocked
                    ? "bg-gradient-to-r from-emerald-500 to-green-600 text-white"
                    : "bg-gradient-to-r from-purple-500 to-blue-500 text-white"
                }`}
              >
                <p className="text-xs font-semibold truncate max-w-[120px]">
                  {getAudioById?.company_name || "Company"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Approve Button */}
        {!isLocked && selectedSongId && (
          <div className="sticky top-4 z-10 mb-6">
            <button
              onClick={handleApproveClick}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold py-3 px-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-[0.98]"
            >
              <CheckCircle2 size={20} />
              Approve Selected Song
              {approvedSongId === selectedSongId && (
                <span className="ml-2 text-xs bg-white/20 px-2 py-0.5 rounded-full">
                  Approved ✓
                </span>
              )}
            </button>
          </div>
        )}

        {/* Songs List */}
        {songs.length > 0 ? (
          <div className="space-y-4">
            {songs.map((song, index) => (
              <div
                key={song.id}
                className={`bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg border transition-all duration-200 active:scale-[0.98] ${
                  isLocked && song.approved
                    ? "border-emerald-300 shadow-emerald-100"
                    : selectedSongId === song.id
                      ? "border-green-400 shadow-green-100"
                      : approvedSongId === song.id
                        ? "border-emerald-300 shadow-emerald-100"
                        : "border-white/20 hover:border-purple-200"
                } ${isLocked ? "cursor-default" : "cursor-pointer"}`}
                onClick={() => togglePlay(index)}
              >
                <div className="flex items-center justify-between mb-3">
                  {/* Radio */}
                  <div
                    className="mr-3"
                    onClick={
                      !isLocked
                        ? (e) => handleSelectSong(song.id, e)
                        : undefined
                    }
                  >
                    <div
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        isLocked
                          ? "cursor-not-allowed opacity-50"
                          : "cursor-pointer"
                      } ${
                        selectedSongId === song.id ||
                        (isLocked && song.approved)
                          ? "border-green-500 bg-green-500"
                          : "border-gray-300 hover:border-gray-400"
                      }`}
                    >
                      {(selectedSongId === song.id ||
                        (isLocked && song.approved)) && (
                        <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                      )}
                    </div>
                  </div>

                  {/* Song Info */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-md ${
                        playingIndex === index
                          ? "bg-gradient-to-br from-purple-500 to-blue-500 text-white"
                          : song.approved
                            ? "bg-gradient-to-br from-emerald-500 to-green-500 text-white"
                            : "bg-gradient-to-br from-gray-100 to-gray-200 text-purple-600"
                      }`}
                    >
                      {playingIndex === index ? (
                        <CirclePause size={24} />
                      ) : (
                        <Play size={24} />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-800 truncate">
                          {song.title}
                        </p>
                        {song.approved && (
                          <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                            <CheckCircle2 size={10} />
                            {isLocked ? "Approved & Locked" : "Approved"}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        {playingIndex === index
                          ? "Now Playing"
                          : isLocked && song.approved
                            ? "Approved Song"
                            : "Tap to play"}
                      </p>
                      <p className="text-xs text-gray-500">
                        {/* {moment(song.uploaded_at).format("YYYY/MM/DD")} */}
                        {moment
                          .utc(song.uploaded_at)
                          .tz("Asia/Kolkata")
                          .format("DD/MM/YYYY HH:mm")}
                      </p>
                    </div>
                  </div>
                </div>

                {/* ✅ Progress (STOP propagation so card click doesn't toggle pause) */}
                {playingIndex === index && (
                  <div
                    className="mt-3 space-y-2"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                      <span>{formatTime(currentTime)}</span>
                      <span>{formatTime(duration)}</span>
                    </div>

                    <input
                      type="range"
                      min="0"
                      max={duration || 0}
                      step="0.01"
                      value={currentTime}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        handleSeekStart();
                      }}
                      onTouchStart={(e) => {
                        e.stopPropagation();
                        handleSeekStart();
                      }}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleProgressChange(e);
                      }}
                      onMouseUp={(e) => {
                        e.stopPropagation();
                        handleSeekEnd(e);
                      }}
                      onTouchEnd={(e) => {
                        e.stopPropagation();
                        handleSeekEnd(e);
                      }}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                      style={{
                        background: `linear-gradient(to right, #8B5CF6 0%, #8B5CF6 ${progressPercent}%, #E5E7EB ${progressPercent}%, #E5E7EB 100%)`,
                      }}
                    />

                    <div className="flex items-end justify-between h-8 px-1">
                      {[...Array(20)].map((_, i) => (
                        <div
                          key={i}
                          className="w-1 bg-gradient-to-t from-purple-400 to-purple-200 rounded-full"
                          style={{
                            height: `${Math.random() * 100 + 20}%`,
                            opacity:
                              duration > 0 && currentTime / duration > i / 20
                                ? 1
                                : 0.3,
                          }}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Approved Song Details */}
                {song.approved && song.approved_at && (
                  <div className="mt-3 pt-3 border-t border-emerald-100">
                    <div className="flex items-center gap-2 text-xs text-emerald-600">
                      <CheckCircle2 size={12} />
                      <span className="font-medium">Approved:</span>
                      {/* <span>{formatDate(song.approved_at)}</span> */}
                      <span>
                        {moment
                          .utc(song.approved_at)
                          .tz("Asia/Kolkata")
                          .format("DD/MM/YYYY HH:mm")}
                      </span>
                      {song.approved_by && (
                        <>
                          <span className="mx-1">•</span>
                          <span>By: {song.approved_by}</span>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Hidden Audio */}
                <audio
                  ref={(el) => (audioRefs.current[index] = el)}
                  src={song.url}
                  preload="auto"
                  onEnded={() => {
                    setPlayingIndex(null);
                    setCurrentTime(0);
                    setDuration(0);
                    isSeekingRef.current = false;
                  }}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Play className="text-purple-500" size={24} />
            </div>
            <p className="text-gray-500 text-lg font-medium mb-2">
              No jingles yet
            </p>
            <p className="text-gray-400 text-sm">
              Your jingles will appear here once they're ready
            </p>
          </div>
        )}

        {/* Now Playing Bar (mobile) */}
        {/* {playingIndex !== null && (
          <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-lg border-t border-gray-200 p-4 shadow-2xl">
            <div className="max-w-md mx-auto">
              <div
                className="mb-3"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max={duration || 0}
                  step="0.01"
                  value={currentTime}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    handleSeekStart();
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                    handleSeekStart();
                  }}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleProgressChange(e);
                  }}
                  onMouseUp={(e) => {
                    e.stopPropagation();
                    handleSeekEnd(e);
                  }}
                  onTouchEnd={(e) => {
                    e.stopPropagation();
                    handleSeekEnd(e);
                  }}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, #8B5CF6 0%, #8B5CF6 ${progressPercent}%, #E5E7EB ${progressPercent}%, #E5E7EB 100%)`,
                  }}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <button
                    onClick={() => togglePlay(playingIndex)}
                    className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-500 text-white rounded-xl flex items-center justify-center shadow-lg"
                  >
                    <CirclePause size={24} />
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-800 truncate">
                      {songs[playingIndex]?.title}
                    </p>
                    <p className="text-xs text-gray-500">Now Playing</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={skipBackward}
                    className="p-2 text-gray-600 hover:text-purple-600 transition-colors"
                  >
                    <SkipBack size={20} />
                  </button>
                  <button
                    onClick={skipForward}
                    className="p-2 text-gray-600 hover:text-purple-600 transition-colors"
                  >
                    <SkipForward size={20} />
                  </button>
                  <button
                    onClick={(e) => toggleLike(songs[playingIndex]?.id, e)}
                    className={`p-2 rounded-xl ${likedSongs[songs[playingIndex]?.id]
                        ? "text-pink-500 bg-pink-50"
                        : "text-gray-400 hover:text-pink-400"
                      }`}
                  >
                  </button>
                </div>
              </div>
            </div>
          </div>
        )} */}
      </div>

      {/* Custom slider styles */}
      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          height: 18px;
          width: 18px;
          border-radius: 50%;
          background: #8b5cf6;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 6px rgba(139, 92, 246, 0.3);
        }

        .slider::-moz-range-thumb {
          height: 18px;
          width: 18px;
          border-radius: 50%;
          background: #8b5cf6;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 6px rgba(139, 92, 246, 0.3);
        }
      `}</style>
    </div>
  );
};

export default MusicPlayer;
